import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { showToast } from '../../../Components/Toast/Toast';
import { FaSearch, FaPaperPlane, FaPaperclip, FaTimes, FaArrowLeft, FaUser, FaUserCircle, FaEllipsisV, FaGift } from 'react-icons/fa';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import './Chat.css';
import chatImage from '../../../assets/images/message-chat-icon.png';
import CustomButton from '../../../Components/CustomButton';
import { getChatList, getChat, sendMessage } from '../../../Services/User/Chat';
import useUserStore from '../../../Stores/UserStore';
import useSocket from '../../../Hooks/useSocket';
import GreetingCardsModal from '../../../Components/GreetingCardsModal/GreetingCardsModal';
import ReportUserModal from '../../../Components/ReportUserModal/ReportUserModal';
import BlockUserModal from '../../../Components/BlockUserModal/BlockUserModal';
import UnmatchUserModal from '../../../Components/UnmatchUserModal/UnmatchUserModal';
import PrivateContentRequestModal from '../../../Components/PrivateContentRequestModal/PrivateContentRequestModal';
import PrivateContentAccessModal from '../../../Components/PrivateContentAccessModal/PrivateContentAccessModal';
import AutoReplyModal from '../../../Components/AutoReplyModal/AutoReplyModal';

const Chat = () => {
  usePageTitle('Chat');
  const navigate = useNavigate();
  const { user } = useUserStore();
  const socket = useSocket();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [autoReply, setAutoReply] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showGreetingCardsModal, setShowGreetingCardsModal] = useState(false);
  const [showReportUserModal, setShowReportUserModal] = useState(false);
  const [showBlockUserModal, setShowBlockUserModal] = useState(false);
  const [showUnmatchUserModal, setShowUnmatchUserModal] = useState(false);
  const [showPrivateContentRequestModal, setShowPrivateContentRequestModal] = useState(false);
  const [showPrivateContentAccessModal, setShowPrivateContentAccessModal] = useState(false);
  const [showAutoReplyModal, setShowAutoReplyModal] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  const handleImageError = (imageId) => {
    setImageErrors(prev => ({ ...prev, [imageId]: true }));
  };

  const renderAvatar = (src, alt, className, imageId) => {
    const hasError = imageErrors[imageId];
    const hasValidSrc = src && src !== 'https://via.placeholder.com/50' && src !== 'https://via.placeholder.com/40';
    
    if (hasError || !hasValidSrc) {
      return <FaUserCircle className={`${className} avatar-icon`} />;
    }
    
    return (
      <img 
        src={src} 
        alt={alt}
        className={className}
        onError={() => handleImageError(imageId)}
      />
    );
  };

  const fetchChatList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getChatList(searchQuery);
      console.log('Chat list response:', response);
      const chatListData = Array.isArray(response.data) ? response.data : [];
      setChatList(chatListData);
    } catch (error) {
      console.error('Error fetching chat list:', error);
      showToast('Failed to load chats', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const fetchMessages = useCallback(async (chatId) => {
    try {
      setLoading(true);
      const response = await getChat(chatId);
      console.log('Messages response:', response);
      const messagesData = Array.isArray(response.data) ? response.data : [];
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showToast('Failed to load messages', 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchChatList();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, fetchChatList]);

  // Socket: Listen for new messages
  useEffect(() => {
    const handleNewMessage = (message) => {
      console.log('New message received:', message);
      
      // Add message to the list if it belongs to the current chat
      if (selectedChat && message.chat_id === selectedChat.id) {
        setMessages(prevMessages => {
          // Avoid duplicates
          const exists = prevMessages.some(msg => msg.id === message.id);
          if (exists) return prevMessages;
          return [...prevMessages, message];
        });
      }
      
      // Update chat list to show latest message
      fetchChatList();
    };

    socket.onNewMessage(handleNewMessage);

    return () => {
      socket.offNewMessage(handleNewMessage);
    };
  }, [selectedChat, socket, fetchChatList]);

  // Socket: Join/Leave chat room when chat is selected
  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
      socket.joinChat(selectedChat.id);

      return () => {
        socket.leaveChat(selectedChat.id);
      };
    }
  }, [selectedChat, socket, fetchMessages]);

  // Socket: Listen for typing indicator
  useEffect(() => {
    const handleTyping = (data) => {
      if (selectedChat && data.chat_id === selectedChat.id && data.user_id !== user?.id) {
        setIsTyping(true);
        
        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        
        // Hide typing indicator after 3 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      }
    };

    socket.onTyping(handleTyping);

    return () => {
      socket.offTyping(handleTyping);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [selectedChat, user, socket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };


  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
  };

  const handleBackToList = () => {
    setSelectedChat(null);
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleDropdownAction = (action) => {
    setShowDropdown(false);
    
    switch(action) {
      case 'report':
        if (selectedChat?.user_id) {
          setShowReportUserModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'play_game':
        showToast('Play game functionality - Coming soon', 'info');
        break;
      case 'unmatch':
        if (selectedChat?.user_id) {
          setShowUnmatchUserModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'greeting_cards':
        if (selectedChat?.user_id) {
          setShowGreetingCardsModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'block':
        if (selectedChat?.user_id) {
          setShowBlockUserModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'reminders':
        if (selectedChat?.id) {
          navigate(`/chat/${selectedChat.id}/reminders`);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'private_content':
        if (selectedChat?.user_id) {
          setShowPrivateContentRequestModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'private_access':
        if (selectedChat?.user_id) {
          setShowPrivateContentAccessModal(true);
        } else {
          showToast('Please select a chat first', 'error');
        }
        break;
      case 'watch_together':
        showToast('Watch together functionality - Coming soon', 'info');
        break;
      default:
        break;
    }
  };

  const handleGiftClick = () => {
    if (selectedChat?.user_id) {
      setShowGreetingCardsModal(true);
    } else {
      showToast('Please select a chat first', 'error');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!selectedChat) {
      showToast('Please select a chat', 'error');
      return;
    }

    if (!messageText.trim() && !selectedFile) {
      showToast('Please enter a message or select a file', 'error');
      return;
    }

    try {
      setSending(true);
      
      // Stop typing indicator
      socket.emitStopTyping(selectedChat.id);
      
      const formData = new FormData();
      formData.append('chat_id', selectedChat.id);
      formData.append('message', messageText);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await sendMessage(formData);
      
      // Add new message to the list if response contains data
      if (response.data) {
        setMessages(prevMessages => {
          // Avoid duplicates
          const exists = prevMessages.some(msg => msg.id === response.data.id);
          if (exists) return prevMessages;
          return [...prevMessages, response.data];
        });
        
        // Emit message via socket for real-time delivery to other user
        socket.sendMessage({
          chat_id: selectedChat.id,
          message: response.data
        });
      }
      
      // Clear input and file
      setMessageText('');
      handleRemoveFile();
      
      showToast('Message sent successfully', 'success');
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleMessageInput = (e) => {
    const value = e.target.value;
    setMessageText(value);
    
    // Emit typing indicator
    if (value.trim() && selectedChat) {
      socket.emitTyping(selectedChat.id);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        socket.emitStopTyping(selectedChat.id);
      }, 2000);
    }
  };

  const renderChatList = () => {
    if (loading && chatList.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">Loading chats...</p>
        </div>
      );
    }

    if (chatList.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No chats found</p>
        </div>
      );
    }

    return chatList.map((chat) => (
      <div
        key={chat.id}
        className={`chat-list-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
        onClick={() => handleSelectChat(chat)}
      >
        <div className="chat-avatar">
          {renderAvatar(
            chat.image ? `${imageBaseUrl}${chat.image}` : null,
            chat.name || 'Chat User',
            'chat-avatar-img',
            `chat-${chat.id}`
          )}
          {chat.unread > 0 && <span className="unread-badge">{chat.unread}</span>}
        </div>
        <div className="chat-info">
          <h6 className="chat-name">{chat.name || `Chat #${chat.id}`}</h6>
          <p className="chat-preview">
            {typeof chat.last_message === 'string' ? chat.last_message : 'lorem ipsum sit amet'}
          </p>
        </div>
        <div className="chat-meta">
          <span className="chat-time">1 Days Ago</span>
        </div>
      </div>
    ));
  };

  const renderMessages = () => {
    if (!selectedChat) {
      return (
        <div className="empty-chat-state">
          <img src={chatImage} alt="Chat" className="empty-state-image" />
          <p className="empty-state-text">The Chat With Users Will Appear Here</p>
        </div>
      );
    }

    if (loading && messages.length === 0) {
      return (
        <div className="empty-chat-state">
          <p className="text-muted">Loading messages...</p>
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="empty-chat-state">
          <img src={chatImage} alt="Chat" className="empty-state-image" />
          <p className="empty-state-text">No messages yet. Start the conversation!</p>
        </div>
      );
    }

    return (
      <div className="messages-container">
        {messages.filter(msg => msg && msg.id).map((message) => {
          const isSent = message.user_id === user?.id;
          const isReceived = !isSent;
          
          return (
            <div
              key={message.id}
              className={`message ${isSent ? 'sent' : 'received'}`}
            >
              {isReceived && (
                <div className="message-avatar-wrapper">
                  {renderAvatar(
                    message.image ? `${imageBaseUrl}${message.image}` : null,
                    message.user_name || 'User',
                    'message-avatar',
                    `message-${message.id}`
                  )}
                </div>
              )}
              <div className="message-content">
                {isReceived && (
                  <p className="message-sender">{message.user_name || 'User'}</p>
                )}
                {message.file && (
                  <div className="message-file">
                    <a href={`${imageBaseUrl}${message.file}`} target="_blank" rel="noopener noreferrer">
                      📎 Attachment
                    </a>
                  </div>
                )}
                {message.message && (
                  <p className="message-text">{String(message.message)}</p>
                )}
                <span className="message-time">{message.time || ''}</span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <>
      <InnerBanner heading="Chat" />
      <section className="py-sm-5 py-4">
        <Container>
          <div className="chat-container">
            {/* Chat List Sidebar */}
            <div className={`chat-sidebar ${selectedChat ? 'has-selected-chat' : ''}`}>
              <div className="chat-sidebar-header">
                <h4>Chats</h4>
                <button 
                  className={`auto-reply-btn ${autoReply ? 'active' : ''}`}
                  onClick={() => setShowAutoReplyModal(true)}
                >
                  Auto Reply
                </button>
              </div>
              
              <div className="chat-search">
                <FaSearch className="search-icon" />
                    <input 
                      type="text" 
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="chat-list">
                {renderChatList()}
              </div>
            </div>

            {/* Chat Messages Area */}
            <div className={`chat-main ${selectedChat ? 'has-selected-chat' : ''}`}>
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="chat-main-header">
                    <button className="back-to-list-btn" onClick={handleBackToList}>
                      <FaArrowLeft />
                    </button>
                    <div className="chat-header-user">
                      {renderAvatar(
                        selectedChat.image ? `${imageBaseUrl}${selectedChat.image}` : null,
                        selectedChat.name || 'User',
                        'chat-header-avatar',
                        `header-${selectedChat.id}`
                      )}
                      <div className="chat-header-info">
                        <h6 className="chat-header-name">{selectedChat.name || `Chat #${selectedChat.id}`}</h6>
                        <span className="chat-header-status">Active</span>
                      </div>
                    </div>
                    
                    {/* Header Actions */}
                    <div className="chat-header-actions">
                      <button className="chat-action-btn" onClick={handleGiftClick}>
                        <FaGift />
                      </button>
                      
                      <div className="chat-dropdown-wrapper" ref={dropdownRef}>
                        <button className="chat-action-btn" onClick={handleDropdownToggle}>
                          <FaEllipsisV />
                        </button>
                        
                        {showDropdown && (
                          <div className="chat-dropdown-menu">
                            <button className="dropdown-item" onClick={() => handleDropdownAction('report')}>
                              Report user
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('play_game')}>
                              Play game
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('unmatch')}>
                              Un match
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('greeting_cards')}>
                              Greeting cards
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('block')}>
                              Block
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('reminders')}>
                              Reminders
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('private_content')}>
                              Send request for the private content
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('private_access')}>
                              Add/Remove private content access
                            </button>
                            <button className="dropdown-item" onClick={() => handleDropdownAction('watch_together')}>
                              Watch together
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="chat-messages">
                    {renderMessages()}
                    {isTyping && (
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="chat-input-container">
                    {selectedFile && (
                      <div className="selected-file">
                        <span>📎 {selectedFile.name}</span>
                        <button onClick={handleRemoveFile} className="remove-file-btn">
                          <FaTimes />
                        </button>
                      </div>
                    )}
                    <form onSubmit={handleSendMessage} className="chat-input-form">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="attach-btn"
                      >
                        <FaPaperclip />
                      </button>
                      <input
                        type="text"
                        placeholder="Type your message..."
                        value={messageText}
                        onChange={handleMessageInput}
                        className="message-input"
                        disabled={sending}
                      />
                      <button type="submit" className="send-btn" disabled={sending}>
                        <FaPaperPlane />
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="empty-chat-state">
                  <img src={chatImage} alt="Chat" className="empty-state-image" />
                  <h5 className="empty-state-title">Find your conversations here</h5>
                  <p className="empty-state-subtitle">
                    When someone likes your profile as well, you will be able to chat with them here.
                  </p>
                  <CustomButton className="mt-3" onClick={() => navigate('/home')}>
                    Back To Browsing
                  </CustomButton>
                </div>
              )}
                </div>
              </div>
        </Container>
      </section>
      
      {/* Greeting Cards Modal */}
      <GreetingCardsModal
        show={showGreetingCardsModal}
        onHide={() => setShowGreetingCardsModal(false)}
        userId={selectedChat?.user_id}
      />
      
      {/* Report User Modal */}
      <ReportUserModal
        show={showReportUserModal}
        onHide={() => setShowReportUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      
      {/* Block User Modal */}
      <BlockUserModal
        show={showBlockUserModal}
        onHide={() => setShowBlockUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      
      {/* Unmatch User Modal */}
      <UnmatchUserModal
        show={showUnmatchUserModal}
        onHide={() => setShowUnmatchUserModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      
      {/* Private Content Request Modal */}
      <PrivateContentRequestModal
        show={showPrivateContentRequestModal}
        onHide={() => setShowPrivateContentRequestModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
      />
      
      {/* Private Content Access Modal */}
      <PrivateContentAccessModal
        show={showPrivateContentAccessModal}
        onHide={() => setShowPrivateContentAccessModal(false)}
        userName={selectedChat?.name || `Chat #${selectedChat?.id}`}
        userId={selectedChat?.user_id}
      />
      
      {/* Auto Reply Modal */}
      <AutoReplyModal
        show={showAutoReplyModal}
        onHide={() => setShowAutoReplyModal(false)}
      />
    </>
  );
};

export default Chat;
